import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
    Loader2,
    AlertCircle,
    Plus,
    CheckCircle,
    Calendar,
    ChevronDown,
    Upload,
    X,
    Download,
    FileSignature,
    ExternalLink,
} from 'lucide-react';
import appliBg from '../../assets/appliBg.svg';
import { Button } from '../../components/ui/button';
import { validateInvestigationFile, validateDocumentOnlyFile } from '../../utils/fileValidation';
import {
    useSubmitEmployeeFormMutation,
    useGetInvestigationByLinkQuery,
    useSubmitGuarantorsMutation,
    useGetConsentDraftQuery,
    useSaveConsentDraftMutation,
} from '../../store/api/apiSlice';

// ─── Types ───────────────────────────────────────────────────────────────────

interface AddressEntry {
    date: string;
    address: string;
}

interface RadioWithDetailsValue {
    answer: 'yes' | 'no' | '';
    list: string;
    reasons: string;
}

type TypeOfWork = '' | 'full_time' | 'part_time' | 'contract' | 'freelance' | 'internship' | 'nysc' | 'volunteer';

type EducationLevel = '' | 'secondary' | 'undergraduate' | 'graduate' | 'postgraduate' | 'doctorate' | 'professional';

interface WorkHistoryEntry {
    employer: string;
    address: string;
    contact: string;
    // Employer contact used to request a reference after submission. An entry with
    // neither email nor phone can't be reached, so no reference request is created.
    email: string;
    phone: string;
    jobTitle: string;
    typeOfWork: TypeOfWork;
    startDate: string;
    endDate: string;
}

interface EducationHistoryEntry {
    institution: string;
    address: string;
    contact: string;
    educationLevel: EducationLevel;
    qualification: string;
    startDate: string;
    endDate: string;
}

interface GuarantorEntry {
    fullName: string;
    // Optional, but recommended — with no email the guarantor only gets a bare SMS link.
    email: string;
    phone: string;
    address: string;
    town: string;
    state: string;
    relationship: string;
}

interface SocialHandlesForm {
    instagram: string;
    twitter: string;
    facebook: string;
    linkedin: string;
    tiktok: string;
    other: string;
}

const SOCIAL_PLATFORMS: { key: keyof SocialHandlesForm; label: string; placeholder: string }[] = [
    { key: 'instagram', label: 'Instagram', placeholder: '@handle' },
    { key: 'twitter', label: 'X (Twitter)', placeholder: '@handle' },
    { key: 'facebook', label: 'Facebook', placeholder: 'Profile name or URL' },
    { key: 'linkedin', label: 'LinkedIn', placeholder: 'Profile URL' },
    { key: 'tiktok', label: 'TikTok', placeholder: '@handle' },
    { key: 'other', label: 'Other', placeholder: 'Any other handle' },
];

interface FormData {
    // Step 0: Personal Identification
    fullLegalName: string;
    previousNames: string;
    alias: string;
    dateOfBirth: string;
    placeOfBirth: string;
    nationality: string;
    otherNationalities: string;
    nationalIdNumber: string;
    stateOfOrigin: string;
    localGovernmentArea: string;
    passportPhoto: File | null;
    governmentId: File | null;
    governmentIdType: '' | 'nin' | 'drivers_license' | 'voters_card' | 'international_passport';
    signedAffidavit: File | null;
    // Optional — only relevant if the employee is already employed. Tagged
    // `letter_of_employment` on submit; never blocks submission.
    letterOfEmployment: File | null;
    // Optional social handles for the social-media review step.
    socialMediaHandles: SocialHandlesForm;
    addresses: AddressEntry[];
    workHistory: WorkHistoryEntry[];
    educationHistory: EducationHistoryEntry[];

    // Step 1: Criminal & Law Enforcement Disclosure
    criminalConviction: RadioWithDetailsValue;
    underInvestigation: RadioWithDetailsValue;
    financialCrimes: RadioWithDetailsValue;
    pleaBargain: RadioWithDetailsValue;
    hasBeenDisengaged: RadioWithDetailsValue;

    // Step 2: Financial & Directorship History
    directorLiquidated: RadioWithDetailsValue;
    disqualifiedDirector: RadioWithDetailsValue;
    currentDirector: RadioWithDetailsValue;

    // Step 3: Professional Discipline
    disciplinaryProceedings: RadioWithDetailsValue;
    licenceRevoked: RadioWithDetailsValue;
    regulatorySanction: RadioWithDetailsValue;

    // Step 4: Political Exposure
    isPep: string;
    hasHeldPublicOffice: RadioWithDetailsValue;
    isRelatedToPep: string;
    hasProcurementInvestigation: string;

    // Step 5: Workplace Conduct
    hasMisconductDismissal: RadioWithDetailsValue;
    hasWorkplaceInvestigation: string;
    hasWorkedWithVulnerableGroups: string;
    hasLeadershipBar: RadioWithDetailsValue;

    // Step 6: Civil Claims
    hasShareholderDisputes: string;
    hasJudgments: string;

    // Step 7: Media Disclosure
    hasAdverseMedia: RadioWithDetailsValue;
    hasPublicAccusation: string;
    hasUndisclosedReputationalMatters: string;

    // Step 8: Conflict of Interest
    hasBusinessConflict: RadioWithDetailsValue;
    hasProcurementRelationship: RadioWithDetailsValue;
    hasCompetingBoard: RadioWithDetailsValue;

    // Step 10: Guarantors (count enforced by plan: Basic = 1, Standard/Pro = 2)
    guarantors: GuarantorEntry[];

    // Step 9: Data Protection & Consent
    consentItems: Record<string, boolean>;
    // Required when the case includes Medical Analysis — consent to share the
    // medical report with the employer.
    consentShareMedicalWithEmployer: boolean;
    // Acknowledgement that the user has read the YourChecka Privacy Policy
    // and Terms of Use. Required before submission alongside the consent items
    // and signed affidavit — it's a separate field because the legal documents
    // are external (help center), not in the consent matrix.
    acceptedPolicies: boolean;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const TABS = [
    'Personal Identification',
    'Address & History',
    'Criminal & Law Enforcement Disclosure',
    'Financial & Directorship History',
    'Professional Discipline',
    'Political Exposure',
    'Workplace Conduct',
    'Civil Claims',
    'Media Disclosure',
    'Conflict Of Interest',
    'Guarantors',
    'Data Protection & Consent',
];

// Empty guarantor row used to seed / pad the guarantor step.
function isValidEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

const EMPTY_GUARANTOR: GuarantorEntry = {
    fullName: '',
    email: '',
    phone: '',
    address: '',
    town: '',
    state: '',
    relationship: '',
};

// Plan rule: Basic (checkType 'standard') needs 1 guarantor, Standard/Pro need 2.
function requiredGuarantorCount(checkType?: string): number {
    return checkType === 'standard' ? 1 : 2;
}

const CONSENT_ITEMS = [
    'Criminal background verification',
    'Employment reference checks',
    'Reference/Guarantor verification',
    'Address verification',
    'Social media review',
    'Court record searches',
    'Surprise Lifestyle Audit',
    'Surprise Procurement Integrity Checks',
];

const TOTAL_STEPS = 12;

// Map step index to the active tab index in the bottom navigation
const STEP_TO_TAB: Record<number, number> = {
    0: 0,
    1: 1,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    10: 10,
    11: 11,
};

const DEFAULT_RADIO_DETAILS: RadioWithDetailsValue = { answer: '', list: '', reasons: '' };

const TYPE_OF_WORK_OPTIONS: { value: TypeOfWork; label: string }[] = [
    { value: '', label: 'Select type of work' },
    { value: 'full_time', label: 'Full-time' },
    { value: 'part_time', label: 'Part-time' },
    { value: 'contract', label: 'Contract' },
    { value: 'freelance', label: 'Freelance' },
    { value: 'internship', label: 'Internship' },
    { value: 'nysc', label: 'NYSC' },
    { value: 'volunteer', label: 'Volunteer' },
];

const EDUCATION_LEVEL_OPTIONS: { value: EducationLevel; label: string }[] = [
    { value: '', label: 'Select education level' },
    { value: 'undergraduate', label: 'Undergraduate' },
    { value: 'graduate', label: 'Graduate' },
    { value: 'postgraduate', label: 'Postgraduate' },
    { value: 'doctorate', label: 'Doctorate (PhD)' },
    { value: 'professional', label: 'Professional Certification' },
];

// educationLevels that backend treats as graduate-tier — drives the qualification UI requirement
const QUALIFICATION_REQUIRED_LEVELS: EducationLevel[] = ['graduate', 'postgraduate', 'doctorate', 'professional'];

const INITIAL_FORM_DATA: FormData = {
    fullLegalName: '',
    previousNames: '',
    alias: '',
    dateOfBirth: '',
    placeOfBirth: '',
    nationality: '',
    otherNationalities: '',
    nationalIdNumber: '',
    stateOfOrigin: '',
    localGovernmentArea: '',
    passportPhoto: null,
    governmentId: null,
    governmentIdType: 'nin',
    signedAffidavit: null,
    letterOfEmployment: null,
    socialMediaHandles: { instagram: '', twitter: '', facebook: '', linkedin: '', tiktok: '', other: '' },
    addresses: [{ date: '', address: '' }],
    workHistory: [],
    educationHistory: [],
    criminalConviction: { ...DEFAULT_RADIO_DETAILS },
    underInvestigation: { ...DEFAULT_RADIO_DETAILS },
    financialCrimes: { ...DEFAULT_RADIO_DETAILS },
    pleaBargain: { ...DEFAULT_RADIO_DETAILS },
    hasBeenDisengaged: { ...DEFAULT_RADIO_DETAILS },
    directorLiquidated: { ...DEFAULT_RADIO_DETAILS },
    disqualifiedDirector: { ...DEFAULT_RADIO_DETAILS },
    currentDirector: { ...DEFAULT_RADIO_DETAILS },
    disciplinaryProceedings: { ...DEFAULT_RADIO_DETAILS },
    licenceRevoked: { ...DEFAULT_RADIO_DETAILS },
    regulatorySanction: { ...DEFAULT_RADIO_DETAILS },
    // Political Exposure
    isPep: '',
    hasHeldPublicOffice: { ...DEFAULT_RADIO_DETAILS },
    isRelatedToPep: '',
    hasProcurementInvestigation: '',
    // Workplace Conduct
    hasMisconductDismissal: { ...DEFAULT_RADIO_DETAILS },
    hasWorkplaceInvestigation: '',
    hasWorkedWithVulnerableGroups: '',
    hasLeadershipBar: { ...DEFAULT_RADIO_DETAILS },
    // Civil Claims
    hasShareholderDisputes: '',
    hasJudgments: '',
    // Media Disclosure
    hasAdverseMedia: { ...DEFAULT_RADIO_DETAILS },
    hasPublicAccusation: '',
    hasUndisclosedReputationalMatters: '',
    // Conflict of Interest
    hasBusinessConflict: { ...DEFAULT_RADIO_DETAILS },
    hasProcurementRelationship: { ...DEFAULT_RADIO_DETAILS },
    hasCompetingBoard: { ...DEFAULT_RADIO_DETAILS },
    // Guarantors — seeded with the max (2); the step shows only the plan-required count
    guarantors: [{ ...EMPTY_GUARANTOR }, { ...EMPTY_GUARANTOR }],
    // Consent
    consentItems: Object.fromEntries(CONSENT_ITEMS.map((item) => [item, false])),
    consentShareMedicalWithEmployer: false,
    acceptedPolicies: false,
};

// ─── Reusable Sub-components ─────────────────────────────────────────────────

function TextInput({
    label,
    value,
    onChange,
    placeholder,
    half = false,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    half?: boolean;
}) {
    return (
        <div className={half ? 'flex-1 min-w-0' : 'w-full'}>
            <label className="block text-sm font-bold text-[#151515] mb-1 leading-6">{label}</label>
            <div className="bg-white border border-[#e0e0e0] rounded-lg h-12 px-3 flex items-center">
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    className="w-full text-xs text-[#4e4d4d] bg-transparent outline-none placeholder:text-[#4e4d4d]"
                />
            </div>
        </div>
    );
}

function DateInput({
    label,
    value,
    onChange,
    placeholder = 'DD/MM/YYYY',
    half = false,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    half?: boolean;
}) {
    const inputRef = useRef<HTMLInputElement>(null);

    // Format ISO date (yyyy-mm-dd) to display format (DD MMM YYYY)
    const formatDisplay = (isoDate: string) => {
        if (!isoDate) return '';
        const d = new Date(isoDate + 'T00:00:00');
        if (isNaN(d.getTime())) return isoDate;
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    return (
        <div className={half ? 'flex-1 min-w-0' : 'w-full'}>
            <label className="block text-sm font-bold text-[#151515] mb-1 leading-6">{label}</label>
            <div
                className="bg-white border border-[#e0e0e0] rounded-lg h-12 px-3 flex items-center cursor-pointer relative"
                onClick={() => inputRef.current?.showPicker()}
            >
                <span className={`flex-1 text-xs ${value ? 'text-[#1b1b1b] font-semibold' : 'text-[#4e4d4d]'}`}>
                    {value ? formatDisplay(value) : placeholder}
                </span>
                <Calendar className="w-4 h-4 text-[#4e4d4d] shrink-0" />
                <input
                    ref={inputRef}
                    type="date"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                />
            </div>
        </div>
    );
}

function DateRangeInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
    const startRef = useRef<HTMLInputElement>(null);
    const endRef = useRef<HTMLInputElement>(null);

    // Parse stored value "yyyy-mm-dd - yyyy-mm-dd" into start/end
    const parts = value.split(' - ');
    const startVal = parts[0] || '';
    const endVal = parts[1] || '';

    const formatDisplay = (isoDate: string) => {
        if (!isoDate) return '';
        const d = new Date(isoDate + 'T00:00:00');
        if (isNaN(d.getTime())) return isoDate;
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const handleChange = (start: string, end: string) => {
        if (start || end) {
            onChange(`${start} - ${end}`);
        } else {
            onChange('');
        }
    };

    const displayText = () => {
        if (!startVal && !endVal) return '';
        return `${formatDisplay(startVal)} - ${formatDisplay(endVal)}`;
    };

    return (
        <div className="w-full">
            <label className="block text-sm font-bold text-[#151515] mb-1 leading-6">{label}</label>
            <div className="bg-white border border-[#e0e0e0] rounded-lg h-12 px-3 flex items-center gap-1">
                <div
                    className="flex-1 flex items-center cursor-pointer relative min-w-0"
                    onClick={() => startRef.current?.showPicker()}
                >
                    <span
                        className={`text-xs truncate ${startVal ? 'text-[#1b1b1b] font-semibold' : 'text-[#4e4d4d]'}`}
                    >
                        {startVal ? formatDisplay(startVal) : 'Start date'}
                    </span>
                    <input
                        ref={startRef}
                        type="date"
                        value={startVal}
                        onChange={(e) => handleChange(e.target.value, endVal)}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                </div>
                <span className="text-xs text-[#4e4d4d] shrink-0">–</span>
                <div
                    className="flex-1 flex items-center cursor-pointer relative min-w-0"
                    onClick={() => endRef.current?.showPicker()}
                >
                    <span className={`text-xs truncate ${endVal ? 'text-[#1b1b1b] font-semibold' : 'text-[#4e4d4d]'}`}>
                        {endVal ? formatDisplay(endVal) : 'End date'}
                    </span>
                    <input
                        ref={endRef}
                        type="date"
                        value={endVal}
                        onChange={(e) => handleChange(startVal, e.target.value)}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                </div>
                <Calendar className="w-4 h-4 text-[#4e4d4d] shrink-0" />
            </div>
        </div>
    );
}

function TextArea({
    placeholder,
    value,
    onChange,
    rows = 3,
}: {
    placeholder?: string;
    value: string;
    onChange: (v: string) => void;
    rows?: number;
}) {
    return (
        <div className="bg-white border border-[#e0e0e0] rounded-lg px-3 py-2">
            <textarea
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                rows={rows}
                className="w-full text-xs text-[#4e4d4d] bg-transparent outline-none resize-none placeholder:text-[#4e4d4d]"
            />
        </div>
    );
}

function RadioGroup({ value, onChange }: { value: string; onChange: (v: 'yes' | 'no') => void }) {
    return (
        <div className="flex items-center gap-5">
            <label className="flex items-center gap-2 cursor-pointer w-[68px]">
                <span className="relative w-6 h-6 flex items-center justify-center">
                    <span
                        className={`absolute inset-[16.67%] rounded-full border ${
                            value === 'yes' ? 'border-[#f60]' : 'border-[#6c6c6c]'
                        }`}
                    />
                    {value === 'yes' && (
                        <span className="absolute inset-[33.33%] rounded-full bg-[#f60] border border-[#f60]" />
                    )}
                </span>
                <input type="radio" checked={value === 'yes'} onChange={() => onChange('yes')} className="sr-only" />
                <span className="text-sm text-[#1b1b1b]">Yes</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer w-[68px]">
                <span className="relative w-6 h-6 flex items-center justify-center">
                    <span
                        className={`absolute inset-[16.67%] rounded-full border ${
                            value === 'no' ? 'border-[#f60]' : 'border-[#6c6c6c]'
                        }`}
                    />
                    {value === 'no' && (
                        <span className="absolute inset-[33.33%] rounded-full bg-[#f60] border border-[#f60]" />
                    )}
                </span>
                <input type="radio" checked={value === 'no'} onChange={() => onChange('no')} className="sr-only" />
                <span className="text-sm text-[#1b1b1b]">No</span>
            </label>
        </div>
    );
}

function RadioWithDetails({
    label,
    value,
    onChange,
    showListField = true,
}: {
    label: string;
    value: RadioWithDetailsValue;
    onChange: (v: RadioWithDetailsValue) => void;
    showListField?: boolean;
}) {
    return (
        <div className="flex flex-col gap-2">
            <label className="text-sm font-bold text-[#151515] leading-6">{label}</label>
            <RadioGroup value={value.answer} onChange={(v) => onChange({ ...value, answer: v })} />
            {value.answer === 'yes' && (
                <>
                    {showListField && (
                        <div className="bg-white border border-[#e0e0e0] rounded-lg h-12 px-3 flex items-center">
                            <input
                                type="text"
                                value={value.list}
                                onChange={(e) => onChange({ ...value, list: e.target.value })}
                                placeholder="List them"
                                className="w-full text-xs text-[#4e4d4d] bg-transparent outline-none placeholder:text-[#4e4d4d]"
                            />
                        </div>
                    )}
                    <TextArea
                        placeholder="Give reasons"
                        value={value.reasons}
                        onChange={(v) => onChange({ ...value, reasons: v })}
                    />
                </>
            )}
        </div>
    );
}

function Checkbox({
    label,
    checked,
    onChange,
}: {
    // Accept either a plain string or arbitrary nodes so callers can embed links
    // (e.g. the "I agree to the Privacy Policy and Terms of Use" acknowledgement).
    label: React.ReactNode;
    checked: boolean;
    onChange: (v: boolean) => void;
}) {
    return (
        <label className="flex items-center gap-2 cursor-pointer">
            <span className="relative w-6 h-6 flex items-center justify-center shrink-0">
                <span
                    className={`absolute inset-[16.67%] rounded border-[1.5px] ${
                        checked ? 'border-[#151515] bg-[#151515]' : 'border-[#333]'
                    }`}
                />
                {checked && <CheckCircle className="absolute w-3 h-3 text-white z-10" />}
            </span>
            <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only" />
            <span className="text-sm text-[#1b1b1b]">{label}</span>
        </label>
    );
}

function Select<T extends string>({
    label,
    value,
    onChange,
    options,
    half = false,
}: {
    label: string;
    value: T;
    onChange: (v: T) => void;
    options: { value: T; label: string }[];
    half?: boolean;
}) {
    return (
        <div className={half ? 'flex-1 min-w-0' : 'w-full'}>
            <label className="block text-sm font-bold text-[#151515] mb-1 leading-6">{label}</label>
            <div className="relative bg-white border border-[#e0e0e0] rounded-lg h-12 px-3 flex items-center">
                <select
                    value={value}
                    onChange={(e) => onChange(e.target.value as T)}
                    className="w-full text-xs text-[#1b1b1b] bg-transparent outline-none appearance-none pr-6 cursor-pointer"
                >
                    {options.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
                <ChevronDown className="absolute right-3 w-4 h-4 text-[#4e4d4d] pointer-events-none" />
            </div>
        </div>
    );
}

function FileInput({
    label,
    helper,
    value,
    onChange,
    accept,
    maxSizeMb = 10,
    onError,
    validate = validateInvestigationFile,
}: {
    label: string;
    helper?: string;
    value: File | null;
    onChange: (file: File | null) => void;
    accept: string;
    maxSizeMb?: number;
    onError?: (message: string) => void;
    // Override the validator for inputs with stricter rules (e.g. affidavit = docs only).
    validate?: (file: File) => string | null;
}) {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleSelect = (file: File | null) => {
        if (!file) {
            onChange(null);
            return;
        }
        const validationError = validate(file);
        if (validationError) {
            onError?.(validationError);
            if (inputRef.current) inputRef.current.value = '';
            return;
        }
        onChange(file);
    };

    return (
        <div className="w-full">
            <label className="block text-sm font-bold text-[#151515] mb-1 leading-6">{label}</label>
            {value ? (
                <div className="bg-white border border-[#e0e0e0] rounded-lg h-12 px-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                    <span className="flex-1 text-xs text-[#1b1b1b] truncate">{value.name}</span>
                    <button
                        type="button"
                        onClick={() => {
                            if (inputRef.current) inputRef.current.value = '';
                            onChange(null);
                        }}
                        className="shrink-0 w-7 h-7 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50"
                        title="Remove file"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    className="w-full bg-white border border-dashed border-[#c8c8c8] rounded-lg h-12 px-3 flex items-center gap-2 text-xs text-[#4e4d4d] hover:border-[#f60] hover:text-[#f60] transition-colors"
                >
                    <Upload className="w-4 h-4 shrink-0" />
                    <span className="flex-1 text-left">{helper || `Upload ${label}`}</span>
                </button>
            )}
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                onChange={(e) => handleSelect(e.target.files?.[0] ?? null)}
                className="hidden"
            />
        </div>
    );
}

// ─── Step Content Components ─────────────────────────────────────────────────

function StepPersonalIdentification({
    formData,
    setFormData,
}: {
    formData: FormData;
    setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}) {
    const updateField = (field: keyof FormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const setPassportPhoto = (file: File | null) => {
        setFormData((prev) => ({ ...prev, passportPhoto: file }));
    };

    const setGovernmentId = (file: File | null) => {
        setFormData((prev) => ({ ...prev, governmentId: file }));
    };

    const setLetterOfEmployment = (file: File | null) => {
        setFormData((prev) => ({ ...prev, letterOfEmployment: file }));
    };

    const updateSocial = (key: keyof SocialHandlesForm, value: string) => {
        setFormData((prev) => ({ ...prev, socialMediaHandles: { ...prev.socialMediaHandles, [key]: value } }));
    };

    return (
        <div className="flex flex-col gap-6 text-left">
            {/* Section: Personal Identification */}
            <div>
                <h2 className="text-xl md:text-2xl font-bold text-[#0f0f0f] leading-8 mb-6 md:mb-[55px]">
                    Personal Identification
                </h2>
                <div className="flex flex-col gap-0">
                    {/* Row 1 */}
                    <div className="flex flex-col sm:flex-row gap-2">
                        <TextInput
                            label="Full Legal Name"
                            value={formData.fullLegalName}
                            onChange={(v) => updateField('fullLegalName', v)}
                            placeholder="Enter your full legal name"
                            half
                        />
                        <TextInput
                            label="Previous Names"
                            value={formData.previousNames}
                            onChange={(v) => updateField('previousNames', v)}
                            placeholder="Enter previous names"
                            half
                        />
                    </div>
                    {/* Row 2 */}
                    <div className="flex flex-col sm:flex-row gap-2 mt-2">
                        <DateInput
                            label="Date of Birth"
                            value={formData.dateOfBirth}
                            onChange={(v) => updateField('dateOfBirth', v)}
                            half
                        />
                        <TextInput
                            label="Place of Birth"
                            value={formData.placeOfBirth}
                            onChange={(v) => updateField('placeOfBirth', v)}
                            placeholder="Enter place of birth"
                            half
                        />
                    </div>
                    {/* Row 3 */}
                    <div className="flex flex-col sm:flex-row gap-2 mt-2">
                        <TextInput
                            label="Nationality"
                            value={formData.nationality}
                            onChange={(v) => updateField('nationality', v)}
                            placeholder="Enter nationality"
                            half
                        />
                        <TextInput
                            label="Other Nationalities"
                            value={formData.otherNationalities}
                            onChange={(v) => updateField('otherNationalities', v)}
                            placeholder="Enter other nationalities"
                            half
                        />
                    </div>
                    {/* Row 4 */}
                    <div className="flex flex-col sm:flex-row gap-2 mt-2">
                        <TextInput
                            label="State of Origin"
                            value={formData.stateOfOrigin}
                            onChange={(v) => updateField('stateOfOrigin', v)}
                            placeholder="Enter state of origin"
                            half
                        />
                        <TextInput
                            label="Local Government Area"
                            value={formData.localGovernmentArea}
                            onChange={(v) => updateField('localGovernmentArea', v)}
                            placeholder="Enter LGA"
                            half
                        />
                    </div>
                    {/* Row 5 */}
                    <div className="flex flex-col sm:flex-row gap-2 mt-2">
                        <TextInput
                            label="National Identification Number"
                            value={formData.nationalIdNumber}
                            onChange={(v) => updateField('nationalIdNumber', v)}
                            placeholder="Enter NIN"
                            half
                        />
                        <TextInput
                            label="Alias"
                            value={formData.alias}
                            onChange={(v) => updateField('alias', v)}
                            placeholder="Nickname / known-as name"
                            half
                        />
                    </div>
                    {/* Row 6: Passport Photo (required) */}
                    <div className="w-full md:w-[487px] mt-2">
                        <FileInput
                            label="Passport Photo"
                            helper="Upload a portrait photo (JPEG/PNG, max 10 MB)"
                            value={formData.passportPhoto}
                            onChange={setPassportPhoto}
                            accept="image/jpeg,image/png,image/jpg"
                            maxSizeMb={10}
                        />
                    </div>
                </div>
            </div>

            {/* Section: Government-issued ID — YourChecka only accepts the NIN
          slip here, so we just collect the upload. The governmentIdType field
          stays pinned to 'nin' in INITIAL_FORM_DATA, no picker shown. */}
            <div className="mt-6">
                <h3 className="text-base font-bold text-[#151515] leading-6 mb-3">Government-issued ID</h3>
                <p className="text-xs text-[#4e4d4d] leading-relaxed mb-4">
                    Upload your NIN slip (PDF or image, max 10 MB).
                </p>
                <FileInput
                    label="NIN slip"
                    helper="PDF or image of your NIN slip"
                    value={formData.governmentId}
                    onChange={setGovernmentId}
                    accept="application/pdf,image/jpeg,image/png,image/jpg,.doc,.docx"
                    maxSizeMb={10}
                />
            </div>

            {/* Section: Letter of Employment (optional) */}
            <div className="mt-6">
                <h3 className="text-base font-bold text-[#151515] leading-6 mb-3">
                    Letter of Employment <span className="text-[#9a9a9a] font-normal">(Optional)</span>
                </h3>
                <p className="text-xs text-[#4e4d4d] leading-relaxed mb-4">
                    If you&apos;re already employed, you can upload your letter of employment. Skip this if it
                    doesn&apos;t apply — it won&apos;t block your submission.
                </p>
                <FileInput
                    label="Letter of employment"
                    helper="PDF or image, max 10 MB"
                    value={formData.letterOfEmployment}
                    onChange={setLetterOfEmployment}
                    accept="application/pdf,image/jpeg,image/png,image/jpg,.doc,.docx"
                    maxSizeMb={10}
                />
            </div>

            {/* Section: Social Media Handles (optional) */}
            <div className="mt-6">
                <h3 className="text-base font-bold text-[#151515] leading-6 mb-3">
                    Social Media Handles <span className="text-[#9a9a9a] font-normal">(Optional)</span>
                </h3>
                <p className="text-xs text-[#4e4d4d] leading-relaxed mb-4">
                    Add any handles you&apos;re comfortable sharing for the social-media review. Leave blank to skip.
                </p>
                <div className="flex flex-col gap-2">
                    {SOCIAL_PLATFORMS.map(({ key, label, placeholder }) => (
                        <TextInput
                            key={key}
                            label={label}
                            value={formData.socialMediaHandles[key]}
                            onChange={(v) => updateSocial(key, v)}
                            placeholder={placeholder}
                            half
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

function StepAddressHistory({
    formData,
    setFormData,
}: {
    formData: FormData;
    setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}) {
    const updateAddress = (index: number, field: keyof AddressEntry, value: string) => {
        setFormData((prev) => ({
            ...prev,
            addresses: prev.addresses.map((addr, i) => (i === index ? { ...addr, [field]: value } : addr)),
        }));
    };

    const addAddress = () => {
        setFormData((prev) => ({
            ...prev,
            addresses: [...prev.addresses, { date: '', address: '' }],
        }));
    };

    const removeAddress = (index: number) => {
        setFormData((prev) => ({
            ...prev,
            addresses: prev.addresses.filter((_, i) => i !== index),
        }));
    };

    const updateWorkHistory = <K extends keyof WorkHistoryEntry>(
        index: number,
        field: K,
        value: WorkHistoryEntry[K],
    ) => {
        setFormData((prev) => ({
            ...prev,
            workHistory: prev.workHistory.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)),
        }));
    };

    const updateEducationHistory = <K extends keyof EducationHistoryEntry>(
        index: number,
        field: K,
        value: EducationHistoryEntry[K],
    ) => {
        setFormData((prev) => ({
            ...prev,
            educationHistory: prev.educationHistory.map((entry, i) =>
                i === index ? { ...entry, [field]: value } : entry,
            ),
        }));
    };

    return (
        <div className="flex flex-col gap-6 text-left">
            {/* Section: Address History */}
            <div className="flex flex-col gap-[23px]">
                <h2 className="text-2xl font-bold text-[#0f0f0f] leading-8">Address History</h2>
                <div className="flex flex-col gap-4">
                    {formData.addresses.map((addr, i) => (
                        <div key={i} className="flex flex-col sm:flex-row gap-2">
                            <div className="w-full sm:w-[232px] shrink-0">
                                <DateRangeInput
                                    label="Date"
                                    value={addr.date}
                                    onChange={(v) => updateAddress(i, 'date', v)}
                                />
                            </div>
                            <TextInput
                                label="Address"
                                value={addr.address}
                                onChange={(v) => updateAddress(i, 'address', v)}
                                placeholder="Enter address"
                                half
                            />
                            {i > 0 && (
                                <button
                                    type="button"
                                    onClick={() => removeAddress(i)}
                                    className="self-end mb-1 shrink-0 w-10 h-10 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                                    title="Remove address"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M3 6h18" />
                                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={addAddress}
                        className="flex items-center justify-center gap-2 bg-[#e0e0e0] rounded-[7px] h-12 w-full sm:w-[264px] text-sm text-[#151515]"
                    >
                        <Plus className="w-6 h-6" />
                        Add new address
                    </button>
                </div>
            </div>

            {/* Section: Work History */}
            <div className="flex flex-col gap-[23px]">
                <h2 className="text-2xl font-bold text-[#0f0f0f] leading-8">Work History</h2>
                <div className="flex flex-col gap-4">
                    {formData.workHistory.map((entry, i) => (
                        <div key={i} className="flex flex-col gap-2 p-4 border border-[#e0e0e0] rounded-lg">
                            <div className="flex flex-col sm:flex-row gap-2">
                                <TextInput
                                    label="Employer"
                                    value={entry.employer}
                                    onChange={(v) => updateWorkHistory(i, 'employer', v)}
                                    placeholder="Company name"
                                    half
                                />
                                <TextInput
                                    label="Job Title"
                                    value={entry.jobTitle}
                                    onChange={(v) => updateWorkHistory(i, 'jobTitle', v)}
                                    placeholder="e.g. Fullstack Developer"
                                    half
                                />
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <TextInput
                                    label="Address"
                                    value={entry.address}
                                    onChange={(v) => updateWorkHistory(i, 'address', v)}
                                    placeholder="Work address"
                                    half
                                />
                                <TextInput
                                    label="Contact"
                                    value={entry.contact}
                                    onChange={(v) => updateWorkHistory(i, 'contact', v)}
                                    placeholder="+234..."
                                    half
                                />
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <TextInput
                                    label="Employer Email"
                                    value={entry.email}
                                    onChange={(v) => updateWorkHistory(i, 'email', v)}
                                    placeholder="hr@company.com"
                                    half
                                />
                                <TextInput
                                    label="Employer Phone"
                                    value={entry.phone}
                                    onChange={(v) => updateWorkHistory(i, 'phone', v)}
                                    placeholder="+234..."
                                    half
                                />
                            </div>
                            <p className="text-xs text-[#717680] -mt-1">
                                We use the employer's email or phone to request a short reference. Leave both blank if
                                you'd rather we not contact them.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <Select<TypeOfWork>
                                    label="Type of Work"
                                    value={entry.typeOfWork}
                                    onChange={(v) => updateWorkHistory(i, 'typeOfWork', v)}
                                    options={TYPE_OF_WORK_OPTIONS}
                                    half
                                />
                                <div className="flex-1" />
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <DateInput
                                    label="Start Date"
                                    value={entry.startDate}
                                    onChange={(v) => updateWorkHistory(i, 'startDate', v)}
                                    half
                                />
                                <DateInput
                                    label="End Date"
                                    value={entry.endDate}
                                    onChange={(v) => updateWorkHistory(i, 'endDate', v)}
                                    half
                                />
                            </div>
                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={() =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            workHistory: prev.workHistory.filter((_, idx) => idx !== i),
                                        }))
                                    }
                                    className="shrink-0 w-10 h-10 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                                    title="Remove entry"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M3 6h18" />
                                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={() =>
                            setFormData((prev) => ({
                                ...prev,
                                workHistory: [
                                    ...prev.workHistory,
                                    {
                                        employer: '',
                                        address: '',
                                        contact: '',
                                        email: '',
                                        phone: '',
                                        jobTitle: '',
                                        typeOfWork: '',
                                        startDate: '',
                                        endDate: '',
                                    },
                                ],
                            }))
                        }
                        className="flex items-center justify-center gap-2 bg-[#e0e0e0] rounded-[7px] h-12 w-full sm:w-[264px] text-sm text-[#151515]"
                    >
                        <Plus className="w-6 h-6" />
                        Add work history
                    </button>
                </div>
            </div>

            {/* Section: Education History */}
            <div className="flex flex-col gap-[23px]">
                <h2 className="text-2xl font-bold text-[#0f0f0f] leading-8">Education History</h2>
                <div className="flex flex-col gap-4">
                    {formData.educationHistory.map((entry, i) => {
                        const requiresQualification = QUALIFICATION_REQUIRED_LEVELS.includes(entry.educationLevel);
                        return (
                            <div key={i} className="flex flex-col gap-2 p-4 border border-[#e0e0e0] rounded-lg">
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <TextInput
                                        label="Institution"
                                        value={entry.institution}
                                        onChange={(v) => updateEducationHistory(i, 'institution', v)}
                                        placeholder="Institution name"
                                        half
                                    />
                                    <TextInput
                                        label="Address"
                                        value={entry.address}
                                        onChange={(v) => updateEducationHistory(i, 'address', v)}
                                        placeholder="Institution address"
                                        half
                                    />
                                </div>
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <TextInput
                                        label="Contact"
                                        value={entry.contact}
                                        onChange={(v) => updateEducationHistory(i, 'contact', v)}
                                        placeholder="+234..."
                                        half
                                    />
                                    <Select<EducationLevel>
                                        label="Education Level"
                                        value={entry.educationLevel}
                                        onChange={(v) => updateEducationHistory(i, 'educationLevel', v)}
                                        options={EDUCATION_LEVEL_OPTIONS}
                                        half
                                    />
                                </div>
                                {requiresQualification && (
                                    <TextInput
                                        label="Qualification"
                                        value={entry.qualification}
                                        onChange={(v) => updateEducationHistory(i, 'qualification', v)}
                                        placeholder="e.g. BSc Computer Science"
                                    />
                                )}
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <DateInput
                                        label="Start Date"
                                        value={entry.startDate}
                                        onChange={(v) => updateEducationHistory(i, 'startDate', v)}
                                        half
                                    />
                                    <DateInput
                                        label="End Date"
                                        value={entry.endDate}
                                        onChange={(v) => updateEducationHistory(i, 'endDate', v)}
                                        half
                                    />
                                </div>
                                <div className="flex justify-end">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                educationHistory: prev.educationHistory.filter((_, idx) => idx !== i),
                                            }))
                                        }
                                        className="shrink-0 w-10 h-10 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                                        title="Remove entry"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="18"
                                            height="18"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M3 6h18" />
                                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                    <button
                        type="button"
                        onClick={() =>
                            setFormData((prev) => ({
                                ...prev,
                                educationHistory: [
                                    ...prev.educationHistory,
                                    {
                                        institution: '',
                                        address: '',
                                        contact: '',
                                        educationLevel: '',
                                        qualification: '',
                                        startDate: '',
                                        endDate: '',
                                    },
                                ],
                            }))
                        }
                        className="flex items-center justify-center gap-2 bg-[#e0e0e0] rounded-[7px] h-12 w-full sm:w-[264px] text-sm text-[#151515]"
                    >
                        <Plus className="w-6 h-6" />
                        Add education history
                    </button>
                </div>
            </div>
        </div>
    );
}

function StepCriminalDisclosure({
    formData,
    setFormData,
}: {
    formData: FormData;
    setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}) {
    return (
        <div>
            <h2 className="text-2xl font-bold text-[#0f0f0f] mb-6 leading-8">Criminal & Law Enforcement Disclosure</h2>
            <div className="flex flex-col gap-4">
                <RadioWithDetails
                    label="Have you ever been convicted of a criminal offence in Nigeria or any other country?"
                    value={formData.criminalConviction}
                    onChange={(v) => setFormData((prev) => ({ ...prev, criminalConviction: v }))}
                    showListField={false}
                />
                <RadioWithDetails
                    label="Are you currently under investigation by any law enforcement or anti-corruption agency?"
                    value={formData.underInvestigation}
                    onChange={(v) => setFormData((prev) => ({ ...prev, underInvestigation: v }))}
                    showListField={false}
                />
                <RadioWithDetails
                    label="Have you ever been charged with financial crimes, fraud, bribery, money laundering, or tax evasion?"
                    value={formData.financialCrimes}
                    onChange={(v) => setFormData((prev) => ({ ...prev, financialCrimes: v }))}
                    showListField={false}
                />
                <RadioWithDetails
                    label="Have you ever entered into a plea bargain in any jurisdiction?"
                    value={formData.pleaBargain}
                    onChange={(v) => setFormData((prev) => ({ ...prev, pleaBargain: v }))}
                    showListField={false}
                />
                <RadioWithDetails
                    label="Have you been disengaged, terminated, or asked to resign from any position?"
                    value={formData.hasBeenDisengaged}
                    onChange={(v) => setFormData((prev) => ({ ...prev, hasBeenDisengaged: v }))}
                    showListField={false}
                />
            </div>
        </div>
    );
}

function StepFinancialDirectorship({
    formData,
    setFormData,
}: {
    formData: FormData;
    setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}) {
    return (
        <div>
            <h2 className="text-2xl font-bold text-[#0f0f0f] mb-6 leading-8">Financial & Directorship History</h2>
            <div className="flex flex-col gap-4">
                <RadioWithDetails
                    label="Have you served as a director of a company that was liquidated or struck off?"
                    value={formData.directorLiquidated}
                    onChange={(v) => setFormData((prev) => ({ ...prev, directorLiquidated: v }))}
                    showListField={false}
                />

                <RadioWithDetails
                    label="Have you been disqualified from acting as a director?"
                    value={formData.disqualifiedDirector}
                    onChange={(v) => setFormData((prev) => ({ ...prev, disqualifiedDirector: v }))}
                    showListField={false}
                />

                <RadioWithDetails
                    label="Are you currently a director, shareholder, or beneficial owner in any company? (List all)"
                    value={formData.currentDirector}
                    onChange={(v) => setFormData((prev) => ({ ...prev, currentDirector: v }))}
                    showListField={false}
                />
            </div>
        </div>
    );
}

function StepProfessionalDiscipline({
    formData,
    setFormData,
}: {
    formData: FormData;
    setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}) {
    return (
        <div>
            <h2 className="text-2xl font-bold text-[#0f0f0f] mb-6 leading-8">Professional Discipline</h2>
            <div className="flex flex-col gap-2 overflow-y-auto">
                <RadioWithDetails
                    label="Are you subject to any professional disciplinary proceedings?"
                    value={formData.disciplinaryProceedings}
                    onChange={(v) => setFormData((prev) => ({ ...prev, disciplinaryProceedings: v }))}
                />
                <RadioWithDetails
                    label="Have you ever had a professional licence revoked or suspended?"
                    value={formData.licenceRevoked}
                    onChange={(v) => setFormData((prev) => ({ ...prev, licenceRevoked: v }))}
                />
                <RadioWithDetails
                    label="Have you been sanctioned by any Nigerian regulatory body?"
                    value={formData.regulatorySanction}
                    onChange={(v) => setFormData((prev) => ({ ...prev, regulatorySanction: v }))}
                />
            </div>
        </div>
    );
}

function StepPoliticalExposure({
    formData,
    setFormData,
}: {
    formData: FormData;
    setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}) {
    const updateField = (field: keyof FormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    return (
        <div>
            <h2 className="text-2xl font-bold text-[#0f0f0f] mb-6 leading-8">Political Exposure</h2>
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                    <label className="text-sm font-bold text-[#151515] leading-6">
                        Are you a Politically Exposed Person (PEP)?
                    </label>
                    <RadioGroup value={formData.isPep} onChange={(v) => updateField('isPep', v)} />
                </div>

                <RadioWithDetails
                    label="Have you held any public office?"
                    value={formData.hasHeldPublicOffice}
                    onChange={(v) => setFormData((prev) => ({ ...prev, hasHeldPublicOffice: v }))}
                    showListField={false}
                />

                <div className="flex flex-col gap-2">
                    <label className="text-sm font-bold text-[#151515] leading-6">
                        Are you related to or closely associated with a PEP?
                    </label>
                    <RadioGroup value={formData.isRelatedToPep} onChange={(v) => updateField('isRelatedToPep', v)} />
                </div>

                <div className="flex flex-col gap-2">
                    <label className="text-sm font-bold text-[#151515] leading-6">
                        Have you been involved in any procurement-related investigation?
                    </label>
                    <RadioGroup
                        value={formData.hasProcurementInvestigation}
                        onChange={(v) => updateField('hasProcurementInvestigation', v)}
                    />
                </div>
            </div>
        </div>
    );
}

function StepWorkplaceConduct({
    formData,
    setFormData,
}: {
    formData: FormData;
    setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}) {
    const updateField = (field: keyof FormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    return (
        <div>
            <h2 className="text-2xl font-bold text-[#0f0f0f] mb-6 leading-8">Workplace Conduct</h2>
            <div className="flex flex-col gap-4">
                <RadioWithDetails
                    label="Have you been dismissed for gross misconduct?"
                    value={formData.hasMisconductDismissal}
                    onChange={(v) => setFormData((prev) => ({ ...prev, hasMisconductDismissal: v }))}
                    showListField={false}
                />

                <div className="flex flex-col gap-2">
                    <label className="text-sm font-bold text-[#151515] leading-6">
                        Have you been subject to any workplace investigation?
                    </label>
                    <RadioGroup
                        value={formData.hasWorkplaceInvestigation}
                        onChange={(v) => updateField('hasWorkplaceInvestigation', v)}
                    />
                </div>

                <div className="flex flex-col gap-2">
                    <label className="text-sm font-bold text-[#151515] leading-6">
                        Have you worked with vulnerable groups?
                    </label>
                    <RadioGroup
                        value={formData.hasWorkedWithVulnerableGroups}
                        onChange={(v) => updateField('hasWorkedWithVulnerableGroups', v)}
                    />
                </div>

                <RadioWithDetails
                    label="Have you been barred from holding a leadership position?"
                    value={formData.hasLeadershipBar}
                    onChange={(v) => setFormData((prev) => ({ ...prev, hasLeadershipBar: v }))}
                    showListField={false}
                />
            </div>
        </div>
    );
}

function StepCivilClaims({
    formData,
    setFormData,
}: {
    formData: FormData;
    setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}) {
    const updateField = (field: keyof FormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const questions = [
        {
            label: 'Have you been involved in shareholder/fiduciary disputes?',
            field: 'hasShareholderDisputes' as keyof FormData,
        },
        {
            label: 'Have you had any judgments entered against you?',
            field: 'hasJudgments' as keyof FormData,
        },
    ];

    return (
        <div>
            <h2 className="text-2xl font-bold text-[#0f0f0f] mb-6 leading-8">Civil Claims</h2>
            <div className="flex flex-col gap-4">
                {questions.map((q) => (
                    <div key={q.field} className="flex flex-col gap-2">
                        <label className="text-sm font-bold text-[#151515] leading-6">{q.label}</label>
                        <RadioGroup value={formData[q.field] as string} onChange={(v) => updateField(q.field, v)} />
                    </div>
                ))}
            </div>
        </div>
    );
}

function StepMediaDisclosure({
    formData,
    setFormData,
}: {
    formData: FormData;
    setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}) {
    const updateField = (field: keyof FormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    return (
        <div>
            <h2 className="text-2xl font-bold text-[#0f0f0f] mb-6 leading-8">Media Disclosure</h2>
            <div className="flex flex-col gap-4">
                <RadioWithDetails
                    label="Have you been the subject of adverse media coverage?"
                    value={formData.hasAdverseMedia}
                    onChange={(v) => setFormData((prev) => ({ ...prev, hasAdverseMedia: v }))}
                    showListField={false}
                />

                <div className="flex flex-col gap-2">
                    <label className="text-sm font-bold text-[#151515] leading-6">
                        Have you been subject to public accusation?
                    </label>
                    <RadioGroup
                        value={formData.hasPublicAccusation}
                        onChange={(v) => updateField('hasPublicAccusation', v)}
                    />
                </div>

                <div className="flex flex-col gap-2">
                    <label className="text-sm font-bold text-[#151515] leading-6">
                        Are there any undisclosed reputational matters?
                    </label>
                    <RadioGroup
                        value={formData.hasUndisclosedReputationalMatters}
                        onChange={(v) => updateField('hasUndisclosedReputationalMatters', v)}
                    />
                </div>
            </div>
        </div>
    );
}

function StepConflictOfInterest({
    formData,
    setFormData,
}: {
    formData: FormData;
    setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}) {
    return (
        <div>
            <h2 className="text-2xl font-bold text-[#0f0f0f] mb-6 leading-8">Conflict Of Interest</h2>
            <div className="flex flex-col gap-2">
                <RadioWithDetails
                    label="Do you have any business conflict of interest?"
                    value={formData.hasBusinessConflict}
                    onChange={(v) => setFormData((prev) => ({ ...prev, hasBusinessConflict: v }))}
                    showListField={false}
                />

                <RadioWithDetails
                    label="Do you have any procurement relationship that may pose a conflict?"
                    value={formData.hasProcurementRelationship}
                    onChange={(v) => setFormData((prev) => ({ ...prev, hasProcurementRelationship: v }))}
                    showListField={false}
                />

                <RadioWithDetails
                    label="Do you sit on any competing boards?"
                    value={formData.hasCompetingBoard}
                    onChange={(v) => setFormData((prev) => ({ ...prev, hasCompetingBoard: v }))}
                    showListField={false}
                />
            </div>
        </div>
    );
}

function StepGuarantors({
    formData,
    setFormData,
    requiredCount,
}: {
    formData: FormData;
    setFormData: React.Dispatch<React.SetStateAction<FormData>>;
    requiredCount: number;
}) {
    const updateGuarantor = (index: number, field: keyof GuarantorEntry, value: string) => {
        setFormData((prev) => {
            const next = prev.guarantors.map((g) => ({ ...g }));
            while (next.length <= index) next.push({ ...EMPTY_GUARANTOR });
            const target = next[index] ?? { ...EMPTY_GUARANTOR };
            next[index] = { ...target, [field]: value } as GuarantorEntry;
            return { ...prev, guarantors: next };
        });
    };

    // Only the plan-required number of guarantors is collected.
    const visible = formData.guarantors.slice(0, requiredCount);
    while (visible.length < requiredCount) visible.push({ ...EMPTY_GUARANTOR });

    return (
        <div>
            <h2 className="text-2xl font-bold text-[#0f0f0f] mb-2 leading-8">Guarantors</h2>
            <p className="text-sm text-[#4e4d4d] mb-4 leading-6">
                Your plan requires <span className="font-bold text-[#151515]">{requiredCount}</span> guarantor
                {requiredCount === 1 ? '' : 's'}. Please provide their details below. At least one guarantor should live
                in the town where you are being employed.
            </p>

            {/* A guarantor is not a referee — employees routinely confuse the two and
                supply a professional reference instead of someone who vouches for them. */}
            <div className="mb-6 flex items-start gap-2.5 rounded-xl border border-[#FF6B35]/30 bg-[#FFF8F4] px-4 py-3">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-[#FF6B35]" />
                <div className="text-sm leading-relaxed text-[#4e4d4d]">
                    <p className="font-bold text-[#151515]">This is a Guarantor, not a Referee.</p>
                    <p className="mt-1">
                        A guarantor personally vouches for you and takes responsibility for your conduct. This is not a
                        professional or character reference. Please make sure the people you list below have agreed to
                        stand as your guarantor.
                    </p>
                    <p className="mt-1">
                        We only contact guarantors between <span className="font-medium text-[#151515]">9:00am</span>{' '}
                        and <span className="font-medium text-[#151515]">7:00pm</span>, so please give numbers they can
                        be reached on during those hours.
                    </p>
                </div>
            </div>

            <div className="flex flex-col gap-8">
                {visible.map((g, i) => (
                    <div key={i} className="flex flex-col gap-4">
                        <h3 className="text-base font-bold text-[#151515]">Guarantor {i + 1}</h3>
                        <TextInput
                            label="Full name"
                            value={g.fullName}
                            onChange={(v) => updateGuarantor(i, 'fullName', v)}
                            placeholder="e.g. Chidinma Okeke"
                        />
                        {/* Email is optional, but without it the guarantor only gets a bare
                            SMS link — the email carries the candidate, company and relationship. */}
                        <div className="flex flex-col gap-1">
                            <TextInput
                                label="Guarantor's email (recommended — we'll email them to confirm)"
                                value={g.email}
                                onChange={(v) => updateGuarantor(i, 'email', v)}
                                placeholder="e.g. chidinma@example.com"
                            />
                            {g.email.trim() !== '' && !isValidEmail(g.email) && (
                                <p className="text-xs text-red-600">Please enter a valid email address.</p>
                            )}
                        </div>
                        {/* Either channel is fine, but a guarantor with neither can't be
                            contacted at all — so at least one is required. */}
                        {!g.email.trim() && !g.phone.trim() && (
                            <p className="-mt-2 text-xs text-[#8a5a3b]">
                                Please provide an email or a phone number so we can contact this guarantor.
                            </p>
                        )}
                        <div className="flex flex-col sm:flex-row gap-4">
                            <TextInput
                                label="Phone number"
                                value={g.phone}
                                onChange={(v) => updateGuarantor(i, 'phone', v)}
                                placeholder="e.g. 08012345678"
                                half
                            />
                            <TextInput
                                label="Relationship"
                                value={g.relationship}
                                onChange={(v) => updateGuarantor(i, 'relationship', v)}
                                placeholder="e.g. Former employer"
                                half
                            />
                        </div>
                        <TextInput
                            label="Address"
                            value={g.address}
                            onChange={(v) => updateGuarantor(i, 'address', v)}
                            placeholder="e.g. 12 Allen Ave, Ikeja"
                        />
                        <div className="flex flex-col sm:flex-row gap-4">
                            <TextInput
                                label="Town / City"
                                value={g.town}
                                onChange={(v) => updateGuarantor(i, 'town', v)}
                                placeholder="e.g. Ikeja"
                                half
                            />
                            <TextInput
                                label="State"
                                value={g.state}
                                onChange={(v) => updateGuarantor(i, 'state', v)}
                                placeholder="e.g. Lagos"
                                half
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function StepConsentAgreement({
    formData,
    setFormData,
    linkToken,
    affidavitError,
    onAffidavitError,
    requireMedicalShareConsent,
}: {
    formData: FormData;
    setFormData: React.Dispatch<React.SetStateAction<FormData>>;
    linkToken: string;
    affidavitError: string;
    onAffidavitError: (message: string) => void;
    requireMedicalShareConsent: boolean;
}) {
    const toggleConsent = (item: string, checked: boolean) => {
        setFormData((prev) => ({
            ...prev,
            consentItems: { ...prev.consentItems, [item]: checked },
        }));
    };

    const setSignedAffidavit = (file: File | null) => {
        setFormData((prev) => ({ ...prev, signedAffidavit: file }));
    };

    const [isDownloadingAffidavit, setIsDownloadingAffidavit] = useState(false);

    const handleDownloadAffidavit = async () => {
        if (!linkToken) return;
        setIsDownloadingAffidavit(true);
        onAffidavitError('');
        try {
            const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) || '/api/v1';
            const res = await fetch(`${apiBase}/investigations/by-link/${linkToken}/affidavit`);
            if (!res.ok) {
                let message = `Failed to download affidavit (HTTP ${res.status})`;
                try {
                    const json = await res.json();
                    if (json?.message) message = json.message;
                } catch {
                    /* response wasn't JSON */
                }
                onAffidavitError(message);
                return;
            }
            const blob = await res.blob();
            const cd = res.headers.get('Content-Disposition') || '';
            const match = cd.match(/filename="?([^";]+)"?/i);
            const fileName = match?.[1] || `affidavit-${linkToken}.pdf`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err: any) {
            onAffidavitError(err?.message || 'Failed to download affidavit. Please try again.');
        } finally {
            setIsDownloadingAffidavit(false);
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <h2 className="text-2xl font-bold text-[#0f0f0f] leading-8">SECURITY CLEARANCE CONSENT AND AGREEMENT.</h2>
            <p className="text-sm text-[#1b1b1b] leading-5">I consent and agree to:</p>
            <div className="flex flex-col gap-4">
                {CONSENT_ITEMS.map((item) => (
                    <Checkbox
                        key={item}
                        label={item}
                        checked={formData.consentItems[item] ?? false}
                        onChange={(v) => toggleConsent(item, v)}
                    />
                ))}
            </div>

            {/* Required only when the case includes Medical Analysis. */}
            {requireMedicalShareConsent && (
                <div className="rounded-xl border border-[#FF6B35]/30 bg-[#FFF8F4] p-5">
                    <Checkbox
                        label="I agree to share my medical report with my employer."
                        checked={formData.consentShareMedicalWithEmployer}
                        onChange={(v) => setFormData((prev) => ({ ...prev, consentShareMedicalWithEmployer: v }))}
                    />
                    <p className="text-xs text-[#8a5a3b] mt-2 pl-7">
                        This check includes a Medical Analysis. You must agree to share the medical report with your
                        employer to proceed.
                    </p>
                </div>
            )}
            <p className="text-sm text-[#1b1b1b] leading-5">
                You understand false declarations may result in termination or withdrawal of offer.
            </p>

            {/* Privacy & Terms acknowledgement — required, distinct from the
          per-activity consent matrix above. Links open the canonical
          YourChecka policy pages on the help center. */}
            <div className="rounded-xl border border-[#e0e0e0] bg-[#fafafa] p-5">
                <h3 className="text-base font-bold text-[#151515] leading-6">Privacy Policy &amp; Terms of Use</h3>
                <p className="text-xs text-[#4e4d4d] leading-relaxed mt-1">
                    Before submitting, please review how YourChecka collects and uses your data and the terms that
                    govern this background check.
                </p>
                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                    <a
                        href="https://help.podus.org/privacy?product=yourchecka"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center justify-center gap-1 text-xs font-semibold text-[#FF6B35] hover:underline px-3 py-2 rounded-lg border border-[#FF6B35]/30 bg-white"
                    >
                        Read the Privacy Policy
                        <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                    <a
                        href="https://help.podus.org/terms?product=yourchecka"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center justify-center gap-1 text-xs font-semibold text-[#1b1b1b] hover:underline px-3 py-2 rounded-lg border border-[#e0e0e0] bg-white"
                    >
                        Read the Terms of Use
                        <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                </div>
                <div className="mt-4 pt-4 border-t border-[#e0e0e0]">
                    <Checkbox
                        label={
                            <span className="text-sm text-[#1b1b1b] leading-relaxed">
                                I have read and agree to the{' '}
                                <a
                                    href="https://help.podus.org/privacy?product=yourchecka"
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="font-semibold text-[#FF6B35] hover:underline"
                                >
                                    Privacy Policy
                                </a>{' '}
                                and{' '}
                                <a
                                    href="https://help.podus.org/terms?product=yourchecka"
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="font-semibold text-[#FF6B35] hover:underline"
                                >
                                    Terms of Use
                                </a>
                                .
                            </span>
                        }
                        checked={formData.acceptedPolicies}
                        onChange={(v) => setFormData((prev) => ({ ...prev, acceptedPolicies: v }))}
                    />
                </div>
            </div>

            {/* Section: Signed Affidavit */}
            <div className="mt-2 rounded-xl border border-[#e0e0e0] bg-[#fafafa] p-5">
                <div className="flex items-start gap-3 mb-4">
                    <div className="w-9 h-9 shrink-0 rounded-full bg-[#FF6B35]/10 flex items-center justify-center">
                        <FileSignature className="w-4 h-4 text-[#FF6B35]" />
                    </div>
                    <div className="min-w-0">
                        <h3 className="text-base font-bold text-[#151515] leading-6">Signed Affidavit</h3>
                        <p className="text-xs text-[#4e4d4d] leading-relaxed mt-1">
                            Download your personalised affidavit, sign it (you and a Commissioner of Oaths), then upload
                            the signed copy below. The signed affidavit is required to submit this form.
                        </p>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                    <button
                        type="button"
                        onClick={handleDownloadAffidavit}
                        disabled={isDownloadingAffidavit || !linkToken}
                        className="bg-[#151515] hover:bg-[#2a2a2a] disabled:opacity-60 text-white px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 shrink-0"
                    >
                        {isDownloadingAffidavit ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Download className="w-4 h-4" />
                        )}
                        Download Affidavit
                    </button>
                    <div className="flex-1 min-w-0">
                        <FileInput
                            label="Upload Signed Affidavit"
                            helper="Signed copy of the affidavit (PDF or Word document, max 10 MB)"
                            value={formData.signedAffidavit}
                            onChange={setSignedAffidavit}
                            accept="application/pdf,.pdf,.doc,.docx"
                            maxSizeMb={10}
                            onError={onAffidavitError}
                            validate={validateDocumentOnlyFile}
                        />
                    </div>
                </div>

                {affidavitError && <p className="mt-3 text-xs text-red-600">{affidavitError}</p>}
            </div>
        </div>
    );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function EmployeeConsent() {
    const { linkToken } = useParams<{ linkToken: string }>();
    const navigate = useNavigate();

    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
    const [showSuccess, setShowSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [affidavitError, setAffidavitError] = useState('');
    const [supportBlock, setSupportBlock] = useState<{
        code: string;
        link?: string;
        email?: string;
        message?: string;
    } | null>(null);
    const [supportCodeCopied, setSupportCodeCopied] = useState(false);

    // ── Save & return later (server-persisted draft) ───────────────────────────
    // Saves the questionnaire answers + current step to the backend, keyed by the
    // public link token, so the employee can resume on ANY device.
    // NOTE: uploaded files (passport photo, government ID, signed affidavit, letter
    // of employment) are not part of the draft — they're only sent at final submit
    // — so they must be re-attached on return.
    const [draftNotice, setDraftNotice] = useState('');
    const [draftRestored, setDraftRestored] = useState(false);
    const draftRestoredRef = useRef(false);

    const {
        data: investigation,
        isLoading: loadingInvestigation,
        error: investigationError,
    } = useGetInvestigationByLinkQuery(linkToken || '', { skip: !linkToken });

    const { data: savedDraft } = useGetConsentDraftQuery(linkToken || '', { skip: !linkToken });
    const [saveConsentDraft, { isLoading: savingDraft }] = useSaveConsentDraftMutation();

    // Restore the saved draft once, after both the investigation and draft load and
    // only if the form hasn't been submitted yet.
    useEffect(() => {
        if (draftRestoredRef.current) return;
        if (!investigation || investigation.isFormSubmitted) return;
        if (!savedDraft || !savedDraft.formData) return;
        draftRestoredRef.current = true;
        setFormData((prev) => ({
            ...INITIAL_FORM_DATA,
            ...(savedDraft.formData as Partial<FormData>),
            // Files can't be restored — keep whatever is currently in state (null).
            passportPhoto: prev.passportPhoto,
            governmentId: prev.governmentId,
            signedAffidavit: prev.signedAffidavit,
            letterOfEmployment: prev.letterOfEmployment,
        }));
        if (typeof savedDraft.currentStep === 'number') setCurrentStep(savedDraft.currentStep);
        setDraftRestored(true);
    }, [investigation, savedDraft]);

    // Persist the current answers + step to the backend (files excluded).
    const persistDraft = async (notify: boolean) => {
        if (!linkToken) return;
        const { passportPhoto, governmentId, signedAffidavit, letterOfEmployment, ...rest } = formData;
        void passportPhoto;
        void governmentId;
        void signedAffidavit;
        void letterOfEmployment;
        try {
            await saveConsentDraft({
                linkToken,
                formData: rest as Record<string, unknown>,
                currentStep,
            }).unwrap();
            if (notify) {
                setDraftNotice('Progress saved. You can close this page and continue later from any device.');
                setTimeout(() => setDraftNotice(''), 5000);
            }
        } catch {
            if (notify) setDraftNotice('Could not save your progress. Please try again.');
        }
    };

    const [submitForm, { isLoading: submitting }] = useSubmitEmployeeFormMutation();
    const [submitGuarantors, { isLoading: submittingGuarantors }] = useSubmitGuarantorsMutation();

    // Plan-driven guarantor requirement (Basic = 1, Standard/Pro = 2).
    const guarantorCount = requiredGuarantorCount(investigation?.checkType);

    useEffect(() => {
        if (!linkToken) {
            navigate('/');
        }
    }, [linkToken, navigate]);

    // Show success view if form was already submitted
    useEffect(() => {
        if (investigation?.isFormSubmitted) {
            setShowSuccess(true);
            if (investigation.supportCode && !supportBlock) {
                setSupportBlock({ code: investigation.supportCode });
            }
        }
    }, [investigation, supportBlock]);

    // Prefill form with employee details from investigation
    useEffect(() => {
        if (investigation && !investigation.isFormSubmitted) {
            setFormData((prev) => ({
                ...prev,
                fullLegalName: [
                    investigation.employeeFirstName,
                    investigation.employeeMiddleName,
                    investigation.employeeLastName,
                ]
                    .filter(Boolean)
                    .join(' '),
                previousNames: investigation.employeeMiddleName || '',
                dateOfBirth: investigation.dateOfBirth || '',
                nationality: investigation.nationality || '',
                otherNationalities: investigation.otherNationalities || '',
                nationalIdNumber: investigation.nationalIdentificationNumber || '',
                stateOfOrigin: investigation.stateOfOrigin || '',
                localGovernmentArea: investigation.localGovernmentArea || '',
                placeOfBirth: investigation.placeOfBirth || '',
            }));
        }
    }, [investigation]);

    const getReadableError = (err: any, fallback = 'Failed to submit form') => {
        // Transport-level failures (RTK Query FETCH_ERROR / TIMEOUT_ERROR, or a raw
        // "Failed to fetch") carry NO backend response body — the request never
        // completed. The usual cause on Submit is an upload that exceeds the server's
        // request-size limit (large phone photos), which drops the connection before a
        // CORS-headed response can be returned, so the browser only sees "Failed to fetch".
        const status = err?.status;
        const rawText = String(err?.error ?? err?.message ?? '');
        const isNetworkError =
            status === 'FETCH_ERROR' ||
            status === 'TIMEOUT_ERROR' ||
            /failed to fetch|networkerror|load failed|network request failed/i.test(rawText);
        if (isNetworkError) {
            return "We couldn't reach the server. This usually happens when the uploaded photos or documents are too large, or your internet connection dropped. Please try smaller images (under a few MB each) on a stable connection, then submit again.";
        }
        // If the server DID respond with a payload-too-large status, say so plainly.
        if (status === 413) {
            return 'Your uploaded files are too large. Please upload smaller images or documents and try again.';
        }

        const serverMessage = err?.data?.message ?? err?.message ?? err;
        if (Array.isArray(serverMessage)) return serverMessage.join(', ');
        if (typeof serverMessage === 'string') return serverMessage;
        if (serverMessage && typeof serverMessage === 'object')
            return serverMessage.message || serverMessage.error || JSON.stringify(serverMessage);
        return fallback;
    };

    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const formScrollRef = useRef<HTMLDivElement>(null);

    const validateStep = (step: number): string[] => {
        const errors: string[] = [];
        const requireRadio = (value: string, label: string) => {
            if (!value) errors.push(label);
        };
        const requireRadioDetails = (value: RadioWithDetailsValue, label: string) => {
            if (!value.answer) errors.push(label);
            if (value.answer === 'yes' && !value.reasons.trim()) errors.push(`${label} — please provide details`);
        };

        switch (step) {
            case 0: // Personal Identification
                if (!formData.fullLegalName.trim()) errors.push('Full Legal Name is required');
                if (!formData.dateOfBirth) errors.push('Date of Birth is required');
                if (!formData.placeOfBirth.trim()) errors.push('Place of Birth is required');
                if (!formData.nationality.trim()) errors.push('Nationality is required');
                if (!formData.stateOfOrigin.trim()) errors.push('State of Origin is required');
                if (!formData.localGovernmentArea.trim()) errors.push('Local Government Area is required');
                if (!formData.passportPhoto) errors.push('Passport Photo is required');
                if (!formData.governmentIdType) errors.push('Government-issued ID type is required');
                if (!formData.governmentId) errors.push('Government-issued ID upload is required');
                break;
            case 1: // Address & History
                formData.addresses.forEach((addr, i) => {
                    if (!addr.date.trim()) errors.push(`Address ${i + 1}: Date is required`);
                    if (!addr.address.trim()) errors.push(`Address ${i + 1}: Address is required`);
                });
                formData.educationHistory.forEach((entry, i) => {
                    if (QUALIFICATION_REQUIRED_LEVELS.includes(entry.educationLevel) && !entry.qualification.trim()) {
                        errors.push(`Education ${i + 1}: Qualification is required for ${entry.educationLevel} level`);
                    }
                });
                break;
            case 2: // Criminal & Law Enforcement Disclosure
                requireRadioDetails(formData.criminalConviction, 'Criminal conviction question');
                requireRadioDetails(formData.underInvestigation, 'Under investigation question');
                requireRadioDetails(formData.financialCrimes, 'Financial crimes question');
                requireRadioDetails(formData.pleaBargain, 'Plea bargain question');
                requireRadioDetails(formData.hasBeenDisengaged, 'Disengagement question');
                break;
            case 3: // Financial & Directorship History
                requireRadioDetails(formData.directorLiquidated, 'Director of liquidated company question');
                requireRadioDetails(formData.disqualifiedDirector, 'Disqualified director question');
                requireRadioDetails(formData.currentDirector, 'Current director/shareholder question');
                break;
            case 4: // Professional Discipline
                requireRadioDetails(formData.disciplinaryProceedings, 'Disciplinary proceedings question');
                requireRadioDetails(formData.licenceRevoked, 'Licence revoked question');
                requireRadioDetails(formData.regulatorySanction, 'Regulatory sanction question');
                break;
            case 5: // Political Exposure
                requireRadio(formData.isPep, 'PEP question is required');
                requireRadioDetails(formData.hasHeldPublicOffice, 'Public office question');
                requireRadio(formData.isRelatedToPep, 'Related to PEP question is required');
                requireRadio(formData.hasProcurementInvestigation, 'Procurement investigation question is required');
                break;
            case 6: // Workplace Conduct
                requireRadioDetails(formData.hasMisconductDismissal, 'Misconduct dismissal question');
                requireRadio(formData.hasWorkplaceInvestigation, 'Workplace investigation question is required');
                requireRadio(formData.hasWorkedWithVulnerableGroups, 'Vulnerable groups question is required');
                requireRadioDetails(formData.hasLeadershipBar, 'Leadership bar question');
                break;
            case 7: // Civil Claims
                requireRadio(formData.hasShareholderDisputes, 'Shareholder disputes question is required');
                requireRadio(formData.hasJudgments, 'Judgments question is required');
                break;
            case 8: // Media Disclosure
                requireRadioDetails(formData.hasAdverseMedia, 'Adverse media question');
                requireRadio(formData.hasPublicAccusation, 'Public accusation question is required');
                requireRadio(
                    formData.hasUndisclosedReputationalMatters,
                    'Undisclosed reputational matters question is required',
                );
                break;
            case 9: // Conflict of Interest
                requireRadioDetails(formData.hasBusinessConflict, 'Business conflict question');
                requireRadioDetails(formData.hasProcurementRelationship, 'Procurement relationship question');
                requireRadioDetails(formData.hasCompetingBoard, 'Competing board question');
                break;
            case 10: // Guarantors
                formData.guarantors.slice(0, guarantorCount).forEach((g, i) => {
                    if (!g.fullName.trim()) {
                        errors.push(`Guarantor ${i + 1}: full name is required`);
                    }
                    if (!g.town.trim()) {
                        errors.push(`Guarantor ${i + 1}: town / city is required`);
                    }
                    // Email and phone are each optional server-side, but a guarantor with
                    // neither can't be contacted at all — no recommendation link can be sent.
                    // So require at least one channel rather than phone specifically.
                    if (!g.email.trim() && !g.phone.trim()) {
                        errors.push(
                            `Guarantor ${i + 1}: please provide an email or a phone number so we can contact them`,
                        );
                    }
                    // If an email is given it must be valid — the API 400s otherwise.
                    if (g.email.trim() && !isValidEmail(g.email)) {
                        errors.push(`Guarantor ${i + 1}: email address is not valid`);
                    }
                });
                break;
            case 11: // Consent
                if (!Object.values(formData.consentItems).every(Boolean)) {
                    errors.push('You must agree to all consent items before submitting');
                }
                if (investigation?.medicalAnalysisIncluded && !formData.consentShareMedicalWithEmployer) {
                    errors.push('You must consent to sharing your medical report with your employer to proceed');
                }
                if (!formData.signedAffidavit) {
                    errors.push('Signed Affidavit is required (download it, sign it, and upload the signed copy)');
                }
                if (!formData.acceptedPolicies) {
                    errors.push('You must read and accept the Privacy Policy and Terms of Use before submitting');
                }
                break;
        }
        return errors;
    };

    const handleNext = () => {
        const errors = validateStep(currentStep);
        setValidationErrors(errors);
        if (errors.length > 0) {
            formScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        if (currentStep < TOTAL_STEPS - 1) {
            // Auto-save progress each time a step is completed.
            persistDraft(false);
            setCurrentStep((prev) => prev + 1);
            formScrollRef.current?.scrollTo({ top: 0 });
        }
    };

    const handleSaveAndReturn = () => {
        persistDraft(true);
    };

    const handlePrevious = () => {
        if (currentStep > 0) {
            setValidationErrors([]);
            setCurrentStep((prev) => prev - 1);
        }
    };

    const handleSubmit = async () => {
        if (!linkToken) return;
        const errors = validateStep(currentStep);
        setValidationErrors(errors);
        if (errors.length > 0) {
            formScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        setSubmitError('');

        try {
            // Guarantors go through a dedicated endpoint. Submit them first so a count /
            // validation error (e.g. wrong number for the plan) blocks before the main
            // questionnaire is committed.
            await submitGuarantors({
                linkToken,
                guarantors: formData.guarantors.slice(0, guarantorCount).map((g) => ({
                    fullName: g.fullName.trim(),
                    // Omit optional contact fields when blank — an empty string fails the
                    // API's email validation and would otherwise drive an SMS to nowhere.
                    ...(g.email.trim() ? { email: g.email.trim() } : {}),
                    ...(g.phone.trim() ? { phone: g.phone.trim() } : {}),
                    address: g.address.trim(),
                    town: g.town.trim(),
                    state: g.state.trim(),
                    relationship: g.relationship.trim(),
                })),
            }).unwrap();

            const result = await submitForm({
                linkToken,
                data: (() => {
                    const fd = new FormData();
                    // Section 1: Personal Identification
                    fd.append('fullLegalName', formData.fullLegalName);
                    fd.append('previousNames', formData.previousNames);
                    fd.append('alias', formData.alias);
                    fd.append('dateOfBirth', formData.dateOfBirth);
                    fd.append('placeOfBirth', formData.placeOfBirth);
                    fd.append('nationality', formData.nationality);
                    fd.append('stateOfOrigin', formData.stateOfOrigin);
                    fd.append('localGovernmentArea', formData.localGovernmentArea);
                    fd.append('otherNationalities', formData.otherNationalities);
                    fd.append('nationalIdentificationNumber', formData.nationalIdNumber);
                    if (formData.passportPhoto) fd.append('passportPhoto', formData.passportPhoto);
                    if (formData.governmentId) fd.append('governmentId', formData.governmentId);
                    if (formData.governmentIdType) fd.append('governmentIdType', formData.governmentIdType);
                    if (formData.signedAffidavit) fd.append('signedAffidavit', formData.signedAffidavit);
                    fd.append('addressHistory', JSON.stringify(formData.addresses));
                    // Include `position` (from Job Title) so the backend can show the
                    // declared role on the employer reference request. email/phone drive
                    // whether a reference request is created for each employer.
                    fd.append(
                        'workHistory',
                        JSON.stringify(formData.workHistory.map((entry) => ({ ...entry, position: entry.jobTitle }))),
                    );
                    fd.append('educationHistory', JSON.stringify(formData.educationHistory));
                    // Section 2: Criminal & Law Enforcement
                    fd.append('hasConviction', formData.criminalConviction.answer);
                    fd.append('convictionDetails', formData.criminalConviction.reasons);
                    fd.append('isUnderInvestigation', formData.underInvestigation.answer);
                    fd.append('investigationDetails', formData.underInvestigation.reasons);
                    fd.append('hasFinancialCrimeCharge', formData.financialCrimes.answer);
                    fd.append('financialCrimeDetails', formData.financialCrimes.reasons);
                    fd.append('hasPleaBargain', formData.pleaBargain.answer);
                    fd.append('pleaBargainDetails', formData.pleaBargain.reasons);
                    fd.append('hasBeenDisengaged', formData.hasBeenDisengaged.answer);
                    fd.append('disengagementDetails', formData.hasBeenDisengaged.reasons);
                    // Section 3: Financial & Directorship
                    fd.append('hasLiquidatedCompany', formData.directorLiquidated.answer);
                    fd.append('liquidatedCompanyDetails', formData.directorLiquidated.reasons);
                    fd.append('hasDirectorDisqualification', formData.disqualifiedDirector.answer);
                    fd.append('directorDisqualificationDetails', formData.disqualifiedDirector.reasons);
                    fd.append('isCurrentDirector', formData.currentDirector.answer);
                    fd.append('currentDirectorshipDetails', formData.currentDirector.reasons);
                    // Section 4: Professional Discipline
                    fd.append('hasDisciplinaryProceedings', formData.disciplinaryProceedings.answer);
                    fd.append('disciplinaryProceedingsDetails', formData.disciplinaryProceedings.reasons);
                    fd.append('hasLicenceRevoked', formData.licenceRevoked.answer);
                    fd.append('licenceRevokedDetails', formData.licenceRevoked.reasons);
                    fd.append(
                        'revokedLicences',
                        formData.licenceRevoked.list ? JSON.stringify([formData.licenceRevoked.list]) : '',
                    );
                    fd.append('hasRegulatoryBodySanction', formData.regulatorySanction.answer);
                    fd.append('regulatoryBodySanctionDetails', formData.regulatorySanction.reasons);
                    // Section 5: Political Exposure
                    fd.append('isPep', formData.isPep);
                    fd.append('hasHeldPublicOffice', formData.hasHeldPublicOffice.answer);
                    fd.append('publicOfficeDetails', formData.hasHeldPublicOffice.reasons);
                    fd.append('isRelatedToPep', formData.isRelatedToPep);
                    fd.append('hasProcurementInvestigation', formData.hasProcurementInvestigation);
                    // Section 6: Workplace Conduct
                    fd.append('hasMisconductDismissal', formData.hasMisconductDismissal.answer);
                    fd.append('misconductDismissalDetails', formData.hasMisconductDismissal.reasons);
                    fd.append('hasWorkplaceInvestigation', formData.hasWorkplaceInvestigation);
                    fd.append('hasWorkedWithVulnerableGroups', formData.hasWorkedWithVulnerableGroups);
                    fd.append('hasLeadershipBar', formData.hasLeadershipBar.answer);
                    fd.append('leadershipBarDetails', formData.hasLeadershipBar.reasons);
                    // Section 7: Civil Claims
                    fd.append('hasShareholderDisputes', formData.hasShareholderDisputes);
                    fd.append('hasJudgments', formData.hasJudgments);
                    // Section 8: Media Disclosure
                    fd.append('hasAdverseMedia', formData.hasAdverseMedia.answer);
                    fd.append('adverseMediaDetails', formData.hasAdverseMedia.reasons);
                    fd.append('hasPublicAccusation', formData.hasPublicAccusation);
                    fd.append('hasUndisclosedReputationalMatters', formData.hasUndisclosedReputationalMatters);
                    // Section 9: Conflict of Interest
                    fd.append('hasBusinessConflict', formData.hasBusinessConflict.answer);
                    fd.append('businessConflictDetails', formData.hasBusinessConflict.reasons);
                    fd.append('hasProcurementRelationship', formData.hasProcurementRelationship.answer);
                    fd.append('procurementRelationshipDetails', formData.hasProcurementRelationship.reasons);
                    fd.append('hasCompetingBoard', formData.hasCompetingBoard.answer);
                    fd.append('competingBoardDetails', formData.hasCompetingBoard.reasons);
                    // Section 10: Consent
                    fd.append(
                        'consentCriminalBackground',
                        String(formData.consentItems['Criminal background verification'] ?? false),
                    );
                    fd.append(
                        'consentEmploymentReference',
                        String(formData.consentItems['Employment reference checks'] ?? false),
                    );
                    fd.append(
                        'consentGuarantorReference',
                        String(formData.consentItems['Reference/Guarantor verification'] ?? false),
                    );
                    fd.append(
                        'consentAddressVerification',
                        String(formData.consentItems['Address verification'] ?? false),
                    );
                    fd.append(
                        'consentSocialMediaReview',
                        String(formData.consentItems['Social media review'] ?? false),
                    );
                    fd.append(
                        'consentCourtRecordSearches',
                        String(formData.consentItems['Court record searches'] ?? false),
                    );
                    fd.append(
                        'consentLifestyleAudit',
                        String(formData.consentItems['Surprise Lifestyle Audit'] ?? false),
                    );
                    fd.append(
                        'consentProcurementIntegrityChecks',
                        String(formData.consentItems['Surprise Procurement Integrity Checks'] ?? false),
                    );
                    fd.append('consentShareMedicalWithEmployer', String(formData.consentShareMedicalWithEmployer));
                    // Social media handles — sent as a JSON string (multipart), only the
                    // platforms the employee actually filled in. Omitted entirely if blank.
                    const filledHandles = Object.fromEntries(
                        Object.entries(formData.socialMediaHandles).filter(([, v]) => v && v.trim().length > 0),
                    );
                    if (Object.keys(filledHandles).length > 0) {
                        fd.append('socialMediaHandles', JSON.stringify(filledHandles));
                    }
                    // Letter of employment — optional. Tagged via the documents/documentTypes
                    // mechanism (file + matching type in the same position).
                    if (formData.letterOfEmployment) {
                        fd.append('documents', formData.letterOfEmployment);
                        fd.append('documentTypes', 'letter_of_employment');
                    }
                    return fd;
                })(),
            }).unwrap();

            if (result?.support) {
                setSupportBlock(result.support);
            } else if (result?.supportCode) {
                setSupportBlock({ code: result.supportCode });
            }
            setShowSuccess(true);
        } catch (error: any) {
            setSubmitError(getReadableError(error));
        }
    };

    const isLastStep = currentStep === TOTAL_STEPS - 1;
    const activeTabIndex = STEP_TO_TAB[currentStep] ?? 0;
    const employeeName = investigation ? `${investigation.employeeFirstName} ${investigation.employeeLastName}` : '';

    // ── Loading State ──────────────────────────────────────────────────────────

    if (loadingInvestigation) {
        return (
            <div className="min-h-screen bg-[#f5f5f5] flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 text-gray-900 animate-spin mx-auto mb-4" />
                    <p className="text-[#4e4d4d]">Loading investigation details...</p>
                </div>
            </div>
        );
    }

    // ── Error State ────────────────────────────────────────────────────────────

    if (investigationError || !investigation) {
        return (
            <div className="min-h-screen bg-[#f5f5f5] flex items-center justify-center px-4">
                <div className="text-center max-w-md">
                    <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-[#151515] mb-2">Invalid Link</h2>
                    <p className="text-[#4e4d4d] mb-6">
                        This investigation link is invalid or has expired. Please contact your employer for a new link.
                    </p>
                    <Button onClick={() => navigate('/')} className="bg-[#151515] hover:bg-[#1b1b1b]">
                        Go to Homepage
                    </Button>
                </div>
            </div>
        );
    }

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <div className="min-h-screen bg-[#f5f5f5] flex items-center justify-center p-4 md:p-10">
            <div className="bg-white rounded-2xl md:rounded-3xl w-full max-w-[1840px] min-h-screen md:min-h-0 md:h-[1021px] relative overflow-hidden flex flex-col">
                {/* ── Decorative Background Image - Top Right ────────────────── */}
                <img
                    src={appliBg}
                    alt=""
                    className="hidden lg:block absolute top-0 right-0 h-[420px] w-auto rounded-tr-3xl object-contain"
                />

                {/* White gradient overlay (left-to-right fade over image) */}
                <div
                    className="hidden lg:block absolute top-0 right-0 h-[420px] w-[726px] rounded-tr-3xl"
                    style={{
                        backgroundImage:
                            'linear-gradient(92deg, rgb(255,255,255) 14%, rgba(255,255,255,0.886) 48%, rgba(255,255,255,0) 110%)',
                    }}
                />

                {/* ── Hero / Greeting Section ────────────────────────────────── */}
                <div className="relative z-10 px-6 md:px-12 lg:px-[156px] pt-8 md:pt-[50px] text-left">
                    <p className="text-xl md:text-2xl font-bold text-[#acacac] leading-9">
                        Hi {investigation.employeeFirstName}
                    </p>
                    <h1 className="text-2xl md:text-[40px] font-bold text-[#151515] leading-tight md:leading-[30px] mt-3">
                        Lets get more done today.
                    </h1>
                </div>

                {/* ── Description ────────────────────────────────────────────── */}
                <p className="relative z-10 px-6 md:px-12 lg:px-[156px] mt-6 md:mt-10 text-sm text-[#4e4d4d] leading-5 text-justify">
                    Your employer has requested a background check as part of their hiring or employment process. This
                    check will verify the information you provide below and may include verification of your identity,
                    employment history, and other relevant credentials. All information you provide will be handled
                    confidentially and in accordance with applicable data protection regulations.
                </p>

                {/* ── Form Content Section ──────────────────────────────────── */}
                <div
                    ref={formScrollRef}
                    className="relative z-10 flex-1 overflow-x-hidden overflow-y-auto text-left px-6 md:px-12 lg:px-[156px] mt-6 md:mt-10 pb-4 scrollbar-hide"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                    {showSuccess ? (
                        /* ── Success State ────────────────────────────────────────── */
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                            <div className="w-[88px] h-[88px] mb-6">
                                <svg viewBox="0 0 88 88" fill="none" className="w-full h-full">
                                    <circle cx="44" cy="44" r="40" fill="#e8f5e9" />
                                    <path
                                        d="M28 44l12 12 20-20"
                                        stroke="#4caf50"
                                        strokeWidth="4"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <circle cx="44" cy="44" r="40" stroke="#4caf50" strokeWidth="2" />
                                </svg>
                            </div>
                            <h2 className="text-2xl font-bold text-[#151515] mb-4">Submission Successful</h2>
                            <p className="text-sm text-black leading-6 max-w-[561px]">
                                Thank you for providing your information and consent. The background check process will
                                now proceed. You will be notified of any updates regarding your background check status.
                                If you have any questions, please contact your employer directly.
                            </p>

                            {supportBlock?.code && (
                                <div className="mt-8 w-full max-w-[561px] rounded-2xl border border-[#FF6B35]/25 bg-[#FFF8F4] p-5 md:p-6 text-left">
                                    <h3 className="text-base font-bold text-[#151515] mb-1">Save your support code</h3>
                                    <p className="text-xs text-[#4e4d4d] leading-relaxed mb-4">
                                        {supportBlock.message ||
                                            'Save your support code. Use it (or the link) to message the YourChecka team about this verification.'}
                                    </p>

                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
                                        <div className="flex-1 flex items-center justify-between gap-3 bg-white border border-[#e0e0e0] rounded-lg px-4 py-3">
                                            <span className="font-mono text-base md:text-lg font-bold text-[#151515] tracking-[0.04em] truncate">
                                                {supportBlock.code}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    try {
                                                        await navigator.clipboard.writeText(supportBlock.code);
                                                        setSupportCodeCopied(true);
                                                        window.setTimeout(() => setSupportCodeCopied(false), 2000);
                                                    } catch {
                                                        /* clipboard rejected — silently fall back */
                                                    }
                                                }}
                                                className="shrink-0 text-xs font-medium text-[#FF6B35] hover:underline"
                                            >
                                                {supportCodeCopied ? 'Copied' : 'Copy'}
                                            </button>
                                        </div>
                                        {supportBlock.link && (
                                            <a
                                                href={supportBlock.link}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="bg-[#151515] hover:bg-[#2a2a2a] text-white px-4 py-3 rounded-lg text-sm font-medium text-center shrink-0"
                                            >
                                                Open support
                                            </a>
                                        )}
                                    </div>

                                    {supportBlock.email && (
                                        <p className="text-xs text-[#4e4d4d]">
                                            Or email{' '}
                                            <a
                                                href={`mailto:${supportBlock.email}`}
                                                className="font-medium text-[#FF6B35] hover:underline"
                                            >
                                                {supportBlock.email}
                                            </a>{' '}
                                            with your code.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        /* ── Step Content ─────────────────────────────────────────── */
                        <>
                            {validationErrors.length > 0 && (
                                <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                                    <p className="text-sm font-bold text-orange-700 mb-2">
                                        Please complete the following:
                                    </p>
                                    <ul className="list-disc list-inside flex flex-col gap-1">
                                        {validationErrors.map((err, i) => (
                                            <li key={i} className="text-sm text-orange-600">
                                                {err}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {submitError && (
                                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm text-red-600">{submitError}</p>
                                </div>
                            )}

                            {currentStep === 0 && (
                                <StepPersonalIdentification formData={formData} setFormData={setFormData} />
                            )}
                            {currentStep === 1 && <StepAddressHistory formData={formData} setFormData={setFormData} />}
                            {currentStep === 2 && (
                                <StepCriminalDisclosure formData={formData} setFormData={setFormData} />
                            )}
                            {currentStep === 3 && (
                                <StepFinancialDirectorship formData={formData} setFormData={setFormData} />
                            )}
                            {currentStep === 4 && (
                                <StepProfessionalDiscipline formData={formData} setFormData={setFormData} />
                            )}
                            {currentStep === 5 && (
                                <StepPoliticalExposure formData={formData} setFormData={setFormData} />
                            )}
                            {currentStep === 6 && (
                                <StepWorkplaceConduct formData={formData} setFormData={setFormData} />
                            )}
                            {currentStep === 7 && <StepCivilClaims formData={formData} setFormData={setFormData} />}
                            {currentStep === 8 && <StepMediaDisclosure formData={formData} setFormData={setFormData} />}
                            {currentStep === 9 && (
                                <StepConflictOfInterest formData={formData} setFormData={setFormData} />
                            )}
                            {currentStep === 10 && (
                                <StepGuarantors
                                    formData={formData}
                                    setFormData={setFormData}
                                    requiredCount={guarantorCount}
                                />
                            )}
                            {currentStep === 11 && (
                                <StepConsentAgreement
                                    formData={formData}
                                    setFormData={setFormData}
                                    linkToken={linkToken || ''}
                                    affidavitError={affidavitError}
                                    onAffidavitError={setAffidavitError}
                                    requireMedicalShareConsent={!!investigation?.medicalAnalysisIncluded}
                                />
                            )}
                        </>
                    )}
                </div>

                {/* Draft restored / saved notices */}
                {!showSuccess && draftRestored && (
                    <div className="relative z-10 mx-6 md:mx-12 lg:mx-[156px] mb-2 rounded-lg border border-[#FF6B35]/30 bg-[#FFF8F4] px-4 py-2.5 text-xs text-[#8a5a3b]">
                        We restored your saved answers. Please re-attach any documents (passport photo, government ID,
                        signed affidavit) before submitting.
                    </div>
                )}
                {!showSuccess && draftNotice && (
                    <div className="relative z-10 mx-6 md:mx-12 lg:mx-[156px] mb-2 rounded-lg border border-green-200 bg-green-50 px-4 py-2.5 text-xs text-green-700">
                        {draftNotice}
                    </div>
                )}

                {/* ── Bottom Navigation ──────────────────────────────────────── */}
                {!showSuccess && (
                    <div className="relative z-10 px-6 md:px-12 lg:px-[156px] py-4 md:py-[34px] flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 border-t border-gray-100 md:border-t-0">
                        {/* Previous Button */}
                        {currentStep === 0 ? (
                            <button
                                type="button"
                                className="bg-[#f1f1f1] rounded-[10px] h-10 px-5 text-sm text-[#151515] shrink-0"
                            >
                                Previous Page
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={handlePrevious}
                                className="bg-[#f1f1f1] rounded-[10px] h-10 px-5 text-sm text-[#151515] shrink-0"
                            >
                                Previous
                            </button>
                        )}

                        {/* Tab Pills */}
                        <div className="flex gap-2 md:gap-4 items-center overflow-x-auto overflow-y-hidden flex-1 h-10 scrollbar-hide">
                            {TABS.map((tab, i) => {
                                const isActive = i === activeTabIndex;
                                return (
                                    <button
                                        key={tab}
                                        type="button"
                                        className={`shrink-0 px-3 md:px-4 py-2 md:py-3 rounded-full text-[10px] md:text-xs text-[#1b1b1b] whitespace-nowrap leading-5 ${
                                            isActive ? 'bg-[#dcdcdc]' : 'border border-[#dcdcdc]'
                                        }`}
                                    >
                                        {tab}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Save & return later */}
                        <button
                            type="button"
                            onClick={handleSaveAndReturn}
                            disabled={savingDraft}
                            className="bg-white border border-[#dcdcdc] rounded-lg h-10 px-4 text-sm font-medium text-[#151515] shrink-0 hover:bg-gray-50 whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {savingDraft && <Loader2 className="w-4 h-4 animate-spin" />}
                            Save &amp; return later
                        </button>

                        {/* Next / Submit Button */}
                        {isLastStep ? (
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={submitting || submittingGuarantors}
                                className="bg-[#151515] rounded-lg h-10 w-full sm:w-[125px] text-sm font-bold text-white text-center shrink-0 disabled:opacity-50"
                            >
                                {submitting || submittingGuarantors ? (
                                    <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                                ) : (
                                    'Submit'
                                )}
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={handleNext}
                                className="bg-[#151515] rounded-lg h-10 w-full sm:w-[125px] text-sm font-bold text-white text-center shrink-0"
                            >
                                Next
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
